export const BRAND_WORKSPACE_QUERY_KEY = "brand";

export const parseBrandWorkspaceSearch = (search: string): string | null => {
  const value = new URLSearchParams(search)
    .get(BRAND_WORKSPACE_QUERY_KEY)
    ?.trim();
  return value || null;
};

export const buildBrandWorkspacePath = (brandId?: string | null): string => {
  if (!brandId) return "/projects";
  const params = new URLSearchParams({ [BRAND_WORKSPACE_QUERY_KEY]: brandId });
  return `/projects?${params.toString()}`;
};

export const buildLegacyProjectRedirectPath = (
  brandId?: string | null,
  search = "",
  hash = "",
): string => {
  const pathname = brandId
    ? `/projects/${encodeURIComponent(brandId)}`
    : "/projects";
  return `${pathname}${search}${hash}`;
};

export type BrandDetailTab = "overview" | "integrations" | "users" | "settings";

export const BRAND_DETAIL_TAB_QUERY_KEY = "tab";

const BRAND_DETAIL_TABS = new Set<BrandDetailTab>([
  "overview",
  "integrations",
  "users",
  "settings",
]);

export const parseBrandDetailTab = (search: string): BrandDetailTab => {
  const value = new URLSearchParams(search).get(BRAND_DETAIL_TAB_QUERY_KEY);
  return value && BRAND_DETAIL_TABS.has(value as BrandDetailTab)
    ? (value as BrandDetailTab)
    : "overview";
};

export const buildBrandDetailPath = (
  brandId: string,
  tab: BrandDetailTab = "overview",
): string => {
  const pathname = `/projects/${encodeURIComponent(brandId)}`;
  return tab === "overview"
    ? pathname
    : `${pathname}?${new URLSearchParams({ [BRAND_DETAIL_TAB_QUERY_KEY]: tab }).toString()}`;
};

export type BusinessGroupRouteDataState = "loading" | "ready" | "error";

/**
 * A plan-limited one-group Workspace has no meaningful directory choice. Route
 * directly to that group's detail page only after both entitlement and group
 * data are ready. Current group count alone must never hide the directory from
 * a plan that supports creating more groups.
 */
export const resolveSingleBusinessGroupDetailPath = ({
  dataState,
  isSingleGroupPlan,
  businessGroupIds,
  requestedBusinessGroupId,
  tab,
}: {
  dataState: BusinessGroupRouteDataState;
  isSingleGroupPlan: boolean;
  businessGroupIds: string[];
  requestedBusinessGroupId?: string | null;
  tab: BrandDetailTab;
}): string | null => {
  if (
    dataState !== "ready" ||
    !isSingleGroupPlan ||
    businessGroupIds.length !== 1
  ) {
    return null;
  }

  const singleBusinessGroupId = businessGroupIds[0];
  // The directory card opens on application accounts today, so bypassing the
  // directory must preserve that landing view instead of exposing the legacy
  // overview tab that is not part of the current detail navigation.
  const destinationTab = tab === "overview" ? "integrations" : tab;
  if (
    requestedBusinessGroupId === singleBusinessGroupId &&
    destinationTab === tab
  ) {
    return null;
  }

  return buildBrandDetailPath(singleBusinessGroupId, destinationTab);
};
